/**
 * PATH: packages/ui/src/qa/QaOwnerVerification.tsx
 * FILE_ID: FE-QA-OWNER-VERIFICATION-001
 * PURPOSE: One "Verified" state and confirm control, shared by both QA front
 *          doors and used for items and areas alike.
 * WHY: Completing work is a claim by whoever did it; the owner confirming it is
 *      a separate signature by a separate population. The operator asked for the
 *      confirmation to read Yes or No on every check and area, so the state has
 *      to be VISIBLE to everyone and ACTIONABLE only to verifiers. The backend
 *      answers 403 to everyone else and carries no retry, which is why the
 *      button is gated on canVerify rather than rendered and allowed to fail.
 *      Deliberately API-free, like QaStatusControl and QaNoteThread: the two
 *      doors own their own clients, and @quotech/ui is the one package both
 *      import, so this is the only place the two can be kept identical.
 * FLOW: caller passes the row's owner_verified* fields and whether this user may
 *       verify -> badge renders Yes/No plus attribution -> the confirm button
 *       calls onVerify(next); the caller writes it and refetches.
 * LANDMINE: owner_verified is the ONLY input for Yes/No. Reopening a completed
 *       row clears verification server-side, so deriving the badge from a status
 *       or caching it past a status change would show a Yes over work that has
 *       gone back in progress.
 */

import { useCallback, useState } from 'react'
import { BadgeCheck, Loader2, ShieldQuestion } from 'lucide-react'

import { formatNoteStamp, noteAuthorLabel } from './QaNoteThread'

/** The read-only verification surface every QA payload now carries. */
export interface QaOwnerVerificationFields {
  owner_verified?: boolean
  owner_verified_at?: string | null
  owner_verified_by_name?: string | null
  /** Server's verdict on THIS reader. Absent means "assume not". */
  can_verify_completion?: boolean
}

// Reads "Verified", not "Owner Verified": the job's project manager may verify
// too, so naming one of the two populations in the badge was misleading.
export const OWNER_VERIFIED_LABEL = 'Verified'

/** The operator asked for this in these words: Yes once verified, No until. */
export function ownerVerifiedLabel(verified?: boolean): 'Yes' | 'No' {
  return verified ? 'Yes' : 'No'
}

/**
 * "Who verified, and when".
 *
 * Empty when there is nothing to attribute, so a caller can render this without
 * first checking whether a verification exists. A verifier whose account has no
 * full name set still gets attributed rather than rendering a bare timestamp.
 */
export function verifierAttribution(
  name?: string | null,
  at?: string | null,
): string {
  const stamp = at ? formatNoteStamp(at) : ''
  if (!name && !stamp) return ''
  if (!stamp) return noteAuthorLabel(name)
  if (!name) return stamp
  return `${noteAuthorLabel(name)} · ${stamp}`
}

export interface QaOwnerVerificationProps extends QaOwnerVerificationFields {
  /** Announced to screen readers, e.g. the item number or the area name. */
  targetLabel: string
  onVerify: (verified: boolean) => void | Promise<void>
  /**
   * Overrides the server's verdict downwards only, for a caller that already
   * knows the user may verify. Never widens it: a false stays false.
   */
  canVerify?: boolean
  /** Read-only for a reason other than permission, e.g. a pending refetch. */
  disabled?: boolean
  className?: string
}

/**
 * The Yes/No state, with a confirm control for actors who may verify.
 *
 * Sits in normal flow and wraps rather than overflowing, so it can be dropped
 * into a dense checklist row or a wide area header without covering either.
 */
export function QaOwnerVerification({
  targetLabel,
  onVerify,
  canVerify,
  disabled = false,
  className,
  owner_verified: ownerVerified,
  owner_verified_at: ownerVerifiedAt,
  owner_verified_by_name: ownerVerifiedByName,
  can_verify_completion: canVerifyCompletion,
}: QaOwnerVerificationProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const verified = !!ownerVerified
  const mayVerify = canVerify ?? !!canVerifyCompletion
  const attribution = verifierAttribution(ownerVerifiedByName, ownerVerifiedAt)

  const toggle = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      await onVerify(!verified)
    } catch {
      // A refusal or a dropped connection must be visible. Silently reverting
      // would read as "the owner un-verified it", which is a different fact.
      setError(
        verified
          ? 'That verification could not be withdrawn. Please try again.'
          : 'That verification could not be saved. Please try again.',
      )
    } finally {
      setBusy(false)
    }
  }, [onVerify, verified])

  const Icon = verified ? BadgeCheck : ShieldQuestion

  return (
    <div
      className={['flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1', className]
        .filter(Boolean)
        .join(' ')}
    >
      <span
        className={[
          'inline-flex min-w-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
          verified ? 'bg-status-good-muted text-status-good' : 'bg-muted text-muted-foreground',
        ].join(' ')}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">{OWNER_VERIFIED_LABEL}</span>
        <span className="font-semibold">{ownerVerifiedLabel(verified)}</span>
      </span>

      {attribution && (
        <span className="min-w-0 truncate text-[11px] text-muted-foreground" title={attribution}>
          {attribution}
        </span>
      )}

      {mayVerify && (
        <button
          type="button"
          onClick={() => void toggle()}
          disabled={disabled || busy}
          // 44px min target: an owner may well confirm this on a phone on site.
          aria-label={
            verified
              ? `Withdraw owner verification for ${targetLabel}`
              : `Confirm owner verified for ${targetLabel}`
          }
          className={[
            'inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition-colors disabled:opacity-50',
            verified
              ? 'border-border text-muted-foreground hover:bg-muted'
              : 'border-status-good/40 bg-status-good-muted text-status-good hover:bg-status-good/15',
          ].join(' ')}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
          )}
          {verified ? 'Withdraw' : 'Confirm'}
        </button>
      )}

      {error && (
        <span role="alert" className="min-w-0 text-[11px] text-destructive">
          {error}
        </span>
      )}
    </div>
  )
}

export default QaOwnerVerification
